import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ACTIONS } from 'constants/permissions';
import { SomeFeatures, defaultErrorMessage } from 'components/WithFeature';

export * from 'constants/permissions';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { Route } from 'react-router-dom';
import { ReactComponent as WarnIcon } from 'assets/svg/warning.svg';
import { ReactComponent as SuccessIcon } from 'assets/svg/success-sparkles.svg';
import Typography from '@mui/material/Typography';
import Button from 'components/Button';
import toast from 'utils/toast';
import { requestAccess } from 'services/permissions';
import Select from './form/Select';

export const usePermission = (module, action = ACTIONS.VIEW) => {
  const user = useSelector((s) => s.user.user);
  return useMemo(() => {
    return hasPermission(user, module, action);
  }, [user, module, action]);
};

const Access = ({ action = ACTIONS.VIEW, module, showError, children }) => {
  const hasAccess = usePermission(module, action);
  const [requested, setRequested] = useState(false);
  const [type, setType] = useState(action);

  useEffect(() => {
    setRequested(false);
    setType(action);
  }, [module, action]);

  const onRequestAccess = async () => {
    try {
      await requestAccess(module + '.' + type);
      toast.success('Access request sent.');
      setRequested(true);
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (hasAccess) return children;
  if (showError) {
    return (
      <Paper
        sx={{
          m: 2,
          p: 2,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {requested ? (
          <Stack alignItems="center" flex={1} textAlign="center">
            <SuccessIcon
              style={{
                marginBottom: 40,
              }}
            />
            <Typography gutterBottom>
              Your request has been successfully sent to the administrator.
            </Typography>
            <Typography>
              This feature will be activated once your request has been
              approved.
            </Typography>
          </Stack>
        ) : (
          <Stack alignItems="center" spacing={3} flex={1} textAlign="center">
            <WarnIcon
              style={{
                transform: 'scale(1.5)',
                marginTop: 20,
                marginBottom: 30,
              }}
            />
            <Typography>
              You do not have permission to view this page.
              <br />
              Please contact your account administrator.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography>Type of access</Typography>
              <Select
                transformValue
                value={type}
                onChange={setType}
                margin="none"
                size="smalll"
                options={[
                  { label: 'View Only', value: 'view' },
                  { label: 'View and Modify', value: 'modify' },
                  {
                    label: 'View, Modify, and Delete',
                    value: 'delete',
                  },
                ]}
              />
              <Button onClick={onRequestAccess}>Request Access</Button>
            </Stack>
          </Stack>
        )}
      </Paper>
    );
  }
  return null;
};

export const SomePermissions = ({ permissions, children }) => {
  const user = useSelector((s) => s.user.user);
  if (!user.user_access) return false;
  if (!permissions) return children;
  const { access_role, permissions: userPermissions } = user.user_access;
  const isAdmin = access_role === 'admin';
  if (isAdmin) return children;
  const hasAccess = permissions.some(
    (p) => userPermissions[p + '.' + ACTIONS.VIEW] === 1,
  );
  if (hasAccess) return children;
  return null;
};

export const ProtectedRoute = ({ features, module, action, ...props }) => {
  return (
    <SomeFeatures features={features} message={defaultErrorMessage}>
      <Access action={action} module={module} showError>
        <Route {...props} />
      </Access>
    </SomeFeatures>
  );
};

export const hasPermission = (user, module, action = ACTIONS.VIEW) => {
  if (!user.user_access) return false;
  const { permissions, access_role } = user.user_access;
  const prop = `${module}.${action}`;
  const isAdmin = access_role === 'admin';
  if (isAdmin && permissions.hasOwnProperty(prop)) return true;

  return permissions[prop] === 1;
};

export default Access;
